import { useRef, useState, type ReactNode } from 'react';
import {
  AgentCard,
  CARD_LINK,
} from '@/components/agent-chat/agent-card.component';
import {
  CardCost,
  CardFooter,
  CardFree,
  CardMeta,
  CardRow,
  CardRows,
  CardShortOfCredits,
  CardText,
} from '@/components/agent-chat/card-parts.component';
import { useCardBalance } from '@/components/agent-chat/use-buy-credits.hook';
import { AppIcon } from '@/components/ui/app-icon';
import { CreditAmount } from '@/components/ui/chips/credit-amount';
import type {
  ConfirmationRequest,
  TryConfirmationIdentity,
} from '@/hooks/use-agent-chat.hook';
import { iconFlask } from '@/lib/icons';
import type { SampleEstimate } from '@/types/custom-playbooks';
import {
  sampleError,
  tryBlocked,
  validSampleEstimate,
} from '@/containers/playbooks/custom-builder/sample-run.util';
import { TryRepairRows } from './try-quote.component';
import { tryRepairStatus, tryShortfall } from './try-quote.util';

/** The card's one sentence: what the Try runs on. */
function trySentence(quote: SampleEstimate) {
  if (quote.records === 0)
    return `No ${quote.brief ? 'brief' : 'records'} to try yet`;
  if (quote.brief) return 'Run the play on 1 brief';
  const one = quote.records === 1;
  const noun =
    quote.fullRun?.recordKind === 'person'
      ? one
        ? 'person'
        : 'people'
      : quote.fullRun?.recordKind === 'company'
        ? one
          ? 'company'
          : 'companies'
        : one
          ? 'record'
          : 'records';
  return `Run the play on ${quote.records} sample ${noun}`;
}

/** A gray line with a credit amount in it. */
function CreditLine({
  children,
  tone,
}: {
  children: ReactNode;
  tone?: 'warning';
}) {
  return (
    <CardMeta tone={tone}>
      <span className='inline-flex flex-wrap items-center gap-x-[4px]'>
        {children}
      </span>
    </CardMeta>
  );
}

const reload = () => window.location.reload();

export function TryChatCard({
  request,
  expired,
  onConfirm,
  onReject,
}: {
  request: ConfirmationRequest;
  expired: boolean;
  onConfirm: (
    selection: undefined,
    expected: TryConfirmationIdentity
  ) => unknown;
  onReject: () => void;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string>();
  const clicked = useRef(false);
  const quote = request.sample;
  const balance = useCardBalance(quote?.balance);
  const valid =
    !!request.action_token &&
    quote &&
    validSampleEstimate(quote) &&
    request.arguments.quote_id === quote.quoteId &&
    request.arguments.play_version === quote.playVersion &&
    request.arguments.credits_max === quote.maxCredits &&
    // Clu's card may name no count; one it names is the quote's, which a
    // small source can hold under five.
    (request.arguments.records === undefined ||
      request.arguments.records === quote.records) &&
    !('test_record' in request.arguments) &&
    !('record_id' in request.arguments) &&
    !('proposal_id' in request.arguments);

  if (!valid)
    return (
      <AgentCard icon={iconFlask} title='Try it' data-try-confirmation>
        <div role='alert'>
          <CardText>Reload required</CardText>
        </div>
        <CardFooter
          cancel={{ onClick: onReject }}
          primary={{
            label: 'Reload',
            workingLabel: 'Reloading',
            onClick: reload,
          }}
        />
      </AgentCard>
    );

  const availableQuote = { ...quote, balance: balance ?? quote.balance };
  const shortfall = tryShortfall(availableQuote);
  const blocked = tryBlocked(availableQuote);
  const status = tryRepairStatus(quote.build);
  // One gray line: what stops the Try, else the Try budget left.
  const note = error ? (
    <div role='alert'>
      <CardMeta tone='danger'>{error}</CardMeta>
    </div>
  ) : expired ? (
    <div role='status'>
      <CardMeta tone='warning'>Quote expired</CardMeta>
    </div>
  ) : shortfall?.of === 'balance' ? (
    <CardShortOfCredits have={shortfall.have} exact />
  ) : shortfall?.of === 'budget' ? (
    <CreditLine tone='warning'>
      Try budget left{' '}
      <span className='inline-flex items-center'>
        <CreditAmount amount={Math.max(0, shortfall.have)} maxDecimals={6} />.
      </span>{' '}
      Raise it to go on.
    </CreditLine>
  ) : status ? (
    <CardMeta tone='warning'>{status}</CardMeta>
  ) : (
    <CreditLine>
      Try budget left{' '}
      <CreditAmount
        amount={Math.max(0, quote.build.remainingCredits)}
        maxDecimals={6}
      />
    </CreditLine>
  );

  const confirm = async () => {
    // A second click leaves the card to the send already in flight.
    if (clicked.current) return;
    if (expired || blocked) return false;
    clicked.current = true;
    setConfirmed(true);
    // A changed or failed confirmation needs a fresh server card.
    try {
      const result = (await onConfirm(undefined, {
        actionToken: request.action_token!,
        quoteId: quote.quoteId,
      })) as { ok?: boolean; aborted?: boolean } | undefined;
      // A send that failed before the action ran restores this card: it can
      // confirm again, as the other confirmation cards do.
      if (result && result.ok === false && !result.aborted) {
        clicked.current = false;
        setConfirmed(false);
      }
      return result;
    } catch {
      // The Try may have started: the card stays locked with the reason.
      setError('Try status unknown. Check runs before trying again.');
      return false;
    }
  };

  return (
    <AgentCard
      icon={iconFlask}
      title='Try it'
      trailing={
        quote.records > 0 ? (
          <CardCost upTo exact amount={quote.maxCredits} short={!!shortfall} />
        ) : null
      }
      data-try-confirmation
    >
      <div className='flex flex-col gap-[4px]'>
        <CardText lines={0}>{trySentence(quote)}</CardText>
        {note}
      </div>
      {quote.byStep.length > 0 && (
        <CardRows label='Try steps'>
          {quote.byStep.map((step, index) => (
            <CardRow
              key={`${step.nodeId}-${index}`}
              label={step.label}
              wrap
              end={
                step.maxCredits > 0 ? (
                  <CardCost exact amount={step.maxCredits} />
                ) : (
                  <CardFree />
                )
              }
            />
          ))}
        </CardRows>
      )}
      <TryRepairRows build={quote.build} />
      <CardFooter
        links={
          shortfall?.of === 'budget' ? (
            <a href='/settings/team' className={CARD_LINK}>
              Try budget
            </a>
          ) : undefined
        }
        cancel={{ onClick: onReject }}
        primary={{
          label: 'Run Try',
          workingLabel: 'Starting',
          disabled: expired || blocked,
          onClick: confirm,
        }}
        short={
          shortfall?.of === 'balance' ? { needed: quote.maxCredits } : undefined
        }
        locked={confirmed}
      />
    </AgentCard>
  );
}

export function TryRequestFailure({
  result,
}: {
  result: Record<string, unknown>;
}) {
  const refusal = sampleError({
    status: 409,
    data: { detail: { ...result, code: result.error } },
  });
  const build = refusal.build && {
    ...refusal.build,
    // Only a list of changes renders; anything else keeps the build's.
    changes: Array.isArray(result.changes)
      ? (result.changes as typeof refusal.build.changes)
      : refusal.build.changes,
  };
  const status = tryRepairStatus(build);
  return (
    <AgentCard
      leading={
        <div className='flex size-[20px] flex-none items-center justify-center rounded-[6px] border border-border bg-background text-destructive'>
          <AppIcon icon={iconFlask} style={{ fontSize: 10 }} />
        </div>
      }
      title="Try didn't run"
    >
      <div className='flex flex-col gap-[4px]'>
        <div role='alert'>
          <CardText>{refusal.message}</CardText>
        </div>
        {status && status !== refusal.message && (
          <CardMeta tone='warning'>{status}</CardMeta>
        )}
      </div>
      <TryRepairRows
        code={refusal.code}
        build={build}
        failure={refusal.failure}
      />
      {refusal.code === 'reload_required' && (
        <CardFooter
          primary={{
            label: 'Reload',
            workingLabel: 'Reloading',
            onClick: reload,
          }}
        />
      )}
    </AgentCard>
  );
}
